/* Iter v34s (#1) — Admin approval queue for karyawan profile changes */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, PageHeader, Button, Badge } from "@/components/ui-kit";
import { Check, X, Clock, FileEdit } from "lucide-react";
import api from "@/lib/api";

export default function ProfileChangeRequests() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending_admin");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/profile-change/admin", { params: { status: statusFilter } });
      setRows(r.data || []);
    } catch (_e) { /* ignore */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const approve = async (id) => {
    if (!window.confirm("Setujui perubahan profil ini? Data karyawan akan diperbarui.")) return;
    try {
      await api.post(`/profile-change/${id}/approve`);
      toast.success("Perubahan disetujui & data ter-update");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const reject = async (id) => {
    const reason = window.prompt("Alasan penolakan (opsional):") || "";
    try {
      await api.post(`/profile-change/${id}/reject`, { reason });
      toast.success("Perubahan ditolak");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  return (
    <div className="p-6 space-y-4" data-testid="profile-change-requests">
      <PageHeader title="Pengajuan Perubahan Profil" subtitle="Setujui atau tolak permintaan perubahan profil dari karyawan" />

      <Card className="p-3 flex items-center gap-3">
        <label className="text-xs uppercase tracking-wider text-stone-600">Status:</label>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border border-stone-300 px-2 py-1 text-xs" data-testid="pcr-status-filter">
          <option value="pending_admin">Menunggu Persetujuan</option>
          <option value="pending_email">Menunggu Verifikasi Email</option>
          <option value="approved">Disetujui</option>
          <option value="rejected">Ditolak</option>
          <option value="cancelled">Dibatalkan</option>
        </select>
        <button onClick={load} className="ml-auto text-xs text-stone-500 hover:text-terra-700">Refresh</button>
      </Card>

      {loading && <Card className="p-8 text-center text-stone-400">Memuat...</Card>}

      {!loading && rows.length === 0 && (
        <Card className="p-12 text-center text-stone-400">
          <FileEdit className="h-12 w-12 mx-auto mb-3 text-stone-300" />
          Tidak ada permintaan {statusFilter}.
        </Card>
      )}

      {!loading && rows.map((r) => (
        <Card key={r.id} className="p-4" data-testid={`pcr-row-${r.id}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <p className="font-semibold text-sm">{r.employee_name || "—"} <span className="text-stone-500 font-normal text-xs">({r.employee_email})</span></p>
                <Badge tone={r.status === "pending_admin" ? "warn" : r.status === "approved" ? "success" : "danger"}>{r.status}</Badge>
              </div>
              <p className="text-[11px] text-stone-500 mb-2">
                Diajukan: {r.submitted_at?.slice(0, 19).replace("T", " ")}
                {r.email_verified_at && <span className="ml-2">· Email-verify: {r.email_verified_at?.slice(0, 19).replace("T", " ")}</span>}
              </p>
              <div className="bg-stone-50 border border-stone-200 p-3 text-xs space-y-1">
                <p className="font-semibold text-stone-700 mb-1.5 text-[11px] uppercase tracking-wider">Perubahan yang Diajukan:</p>
                {Object.entries(r.patch || {}).map(([k, v]) => (
                  <div key={k} className="flex gap-2">
                    <span className="text-stone-500 w-32 flex-shrink-0">{k}:</span>
                    <span className="text-stone-900 font-medium break-all">{String(v)}</span>
                  </div>
                ))}
              </div>
            </div>
            {r.status === "pending_admin" && (
              <div className="flex flex-col gap-2 flex-shrink-0">
                <Button onClick={() => approve(r.id)} className="!py-1.5 !px-3 bg-emerald-600 hover:bg-emerald-700" data-testid={`pcr-approve-${r.id}`}>
                  <Check className="h-3.5 w-3.5" /> Setujui
                </Button>
                <Button onClick={() => reject(r.id)} variant="ghost" className="!py-1.5 !px-3 text-red-600 hover:bg-red-50" data-testid={`pcr-reject-${r.id}`}>
                  <X className="h-3.5 w-3.5" /> Tolak
                </Button>
              </div>
            )}
          </div>
        </Card>
      ))}
    </div>
  );
}
